/**
 * Missing-translation guard.
 *
 * When a locale key is missing, Shopify's `t` filter prints "Translation missing: <locale>.<key>".
 * This script keeps that text away from customers and reports each distinct key once with
 * console.warn (key + page path).
 *
 * Only Shopify's form is matched: the prefix (any case), a colon, then a dotted "<locale>.<key>".
 * Prose such as "Translation missing: this item" is left alone.
 *
 * Replacement rule: the matched text is replaced by a fallback built from the key's last segment
 * ("fr.products.product.add_to_cart" -> "Add to cart"). A trailing plural form (".one", ".other")
 * uses the segment before it, and an "_html" suffix is dropped. If the segment has no letters, or
 * is a single 8+ character token mixing letters and digits (an id or hash), the text is removed.
 *
 * Covered: text nodes (not script/style/noscript/template/textarea), the document title,
 * user-visible attributes (placeholder, title, alt, label, aria-label/-description/-placeholder/
 * -valuetext, value of button/submit/reset inputs), data-* attributes (the theme copies these into
 * text later), and the string values of window.theme (built from `| t` in layout/theme.liquid).
 * Content inserted later (cart drawer, Section Rendering API, quick views, theme editor) is handled
 * by one MutationObserver on added nodes and text changes.
 *
 * Intentionally not observed: attributes set later with setAttribute, input.value assigned later,
 * changes in <head> (including document.title) after start, and a message split across adjacent
 * text nodes. The theme builds those values from data-* attributes and window.theme, which are
 * already cleaned when they are read, so watching attributes or <head> would add cost on every
 * mutation for no customer-visible gain. Textarea content and other form values are never
 * rewritten because they are submitted with the form.
 */
(function () {
  'use strict';

  if (window.I18nGuard) return;

  const PROBE = /translation missing/i;
  const MISSING = /translation missing:\s*([a-z0-9_-]+(?:\.[a-z0-9_-]+)+)/gi;
  const PLURAL_FORMS = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);
  const SKIPPED_PARENTS = new Set(['script', 'style', 'noscript', 'template', 'textarea']);
  const VISIBLE_ATTRIBUTES = new Set([
    'placeholder',
    'title',
    'alt',
    'label',
    'aria-label',
    'aria-description',
    'aria-placeholder',
    'aria-valuetext'
  ]);
  const BUTTON_INPUT = /^(button|submit|reset)$/i;
  const reported = new Set();

  function report(key) {
    if (reported.has(key)) return;
    reported.add(key);
    console.warn(`[i18n] Missing translation "${key}" on ${window.location.pathname}`);
  }

  function fallbackFor(key) {
    const segments = key.split('.');
    let segment = segments.pop();
    if (PLURAL_FORMS.has(segment.toLowerCase()) && segments.length > 1) segment = segments.pop();
    segment = segment.replace(/_html$/i, '');
    if (!/[a-z]/i.test(segment)) return '';
    if (/^[a-z0-9]{8,}$/i.test(segment) && /\d/.test(segment)) return '';
    const words = segment
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  function clean(value) {
    return value.replace(MISSING, (match, key) => {
      report(key);
      return fallbackFor(key);
    });
  }

  function fixText(node) {
    const parent = node.parentNode;
    if (parent && SKIPPED_PARENTS.has(parent.localName)) return;
    const text = node.data;
    if (!PROBE.test(text)) return;
    const next = clean(text);
    if (next !== text) node.data = next;
  }

  function isVisibleAttribute(element, name) {
    if (name.startsWith('data-') || VISIBLE_ATTRIBUTES.has(name)) return true;
    return name === 'value' && element.localName === 'input' && BUTTON_INPUT.test(element.getAttribute('type') || '');
  }

  function fixAttributes(element) {
    const attributes = element.attributes;
    for (let i = 0; i < attributes.length; i++) {
      const { name, value } = attributes[i];
      if (!PROBE.test(value) || !isVisibleAttribute(element, name)) continue;
      const next = clean(value);
      if (next !== value) element.setAttribute(name, next);
    }
  }

  function sweep(root) {
    if (!root) return;
    if (root.nodeType === 3) {
      fixText(root);
      return;
    }
    const element = root.nodeType === 9 ? root.documentElement : root;
    if (!element || element.nodeType !== 1 || !PROBE.test(element.outerHTML)) return;

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let node = element;
    do {
      if (node.nodeType === 1) {
        if (node.attributes.length) fixAttributes(node);
      } else {
        fixText(node);
      }
    } while ((node = walker.nextNode()));
  }

  function cleanStrings(object, depth) {
    if (!object || typeof object !== 'object' || depth > 3) return;
    Object.keys(object).forEach(name => {
      const value = object[name];
      if (typeof value === 'string') {
        if (PROBE.test(value)) object[name] = clean(value);
      } else if (Object.prototype.toString.call(value) === '[object Object]') {
        cleanStrings(value, depth + 1);
      }
    });
  }

  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'characterData') {
        fixText(record.target);
        continue;
      }
      const added = record.addedNodes;
      for (let i = 0; i < added.length; i++) {
        if (added[i].isConnected) sweep(added[i]);
      }
    }
    observer.takeRecords();
  });

  function start() {
    sweep(document);
    cleanStrings(window.theme, 0);
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  window.I18nGuard = {
    sweep,
    disconnect: () => observer.disconnect()
  };

  document.addEventListener('shopify:section:load', event => sweep(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
